/**
 * Sesotho: Contacts
 *
 * What this file covers: The contact book: contacts, fields, tags, segments, imports, consent and erasing a
 * person's data.
 *
 * Tone: Clear and careful. Consent and erasure wording is legal in effect: say exactly what happens to the
 * person's data.
 *
 * Sesotho: Everyday Sesotho as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const contacts: Catalog = {
  'contacts.title': 'Bao u ikopanyang le bona',
  'contacts.optedIn': 'O dumetse',
  'contacts.optedOut': 'O ikhulule',
  'contacts.actions.importCsv': 'Kenya CSV',
  'contacts.loadError': 'Ha ea khona ho kenya bao u ikopanyang le bona.',
  'contacts.shown': '{{count}} e bontšitsoe',
  'contacts.columns.optIn': 'Ho lumela',
  'contacts.optIn.in': 'O lumetse',
  'contacts.optIn.out': 'O ikhulisitse',
  'contacts.modal.delete.title': 'Hlakola motho?',
  'contacts.toast.importQueuedTitle': 'Ho kenya CSV',
  'contacts.toast.importFailed': 'Ho kenya ho hlolehile',
  'contacts.drawer.new': 'Motho e mocha',
  'contacts.drawer.edit': 'Fetola motho',
  'contacts.form.tagsHint': 'Tobetsa Enter kamora letšoao le leng le le leng.',
  'contacts.form.optIn': 'O lumetse',
  'contacts.view.notFound': 'Motho ha a fumanehe.',
  'contacts.view.header.noOptIn': 'ha ho tumello',
  'contacts.view.backToAll': 'Bohle bao u ikopanyang le bona',
  'contacts.view.tabs.profile': 'Boitsebiso',
  'contacts.view.tabs.conversations': 'Lipuisano',
  'contacts.view.tabs.danger': 'Kotsi',
  'contacts.view.profile.locale': 'Sebaka',
  'contacts.view.tags.empty': 'Ha ho matšoao hajoale.',
  'contacts.view.conversations.empty': 'Ha ho lipuisano le motho enoa.',
  'contacts.view.conversations.noPreview': '(ha ho ponelopele)',
  'contacts.view.danger.markOptOut': 'Tšoaea joalo ka ea ikhulisitseng',
  'contacts.view.danger.markOptIn': 'Tšoaea joalo ka ea lumetseng',
  'contacts.view.danger.deleteContact': 'Hlakola motho',
  'contacts.view.danger.modal.delete.irreversible': 'Sena se ke ke sa khutlisoa.',
  'contacts.view.danger.toast.deleteFailed': 'Ha ea khona ho hlakola motho.',
  'contacts.actions.import': 'Kenya',
  'contacts.actions.create': 'Theha moamohedi',
  'contacts.export.csv': 'Ntša e le CSV',
  'contacts.export.xlsx': 'Ntša e le Excel (.xlsx)',
  'contacts.columns.contact': 'Moamohedi',
  'contacts.filters.clear': 'Hlakola lisefo',
  'contacts.filters.tag.option': '{{tag}} ({{count}})',
  'contacts.rowsPerPage': 'Mela ka leqephe',
  'contacts.form.tab.details': 'Lintlha',
  'contacts.form.tab.notes': 'Lintlha tse ngotsoeng',
  'contacts.form.fullName': 'Lebitso le felletseng',
  'contacts.form.phone': 'Nomoro ea mohala',
  'contacts.form.save': 'Boloka moamohedi',
  'contacts.bulk.count': '{{count}} e khethiloeng',
  'contacts.bulk.add': 'Kenya letśoao',
  'contacts.bulk.remove': 'Tlosa letśoao',
  'contacts.bulk.clear': 'Hlakola khetho',
  'contacts.tags.add': 'Kenya letśoao',
  'contacts.showingOf': 'Ho bontśoa {{count}} ho {{total}}',
  'contacts.filters.title': 'Lisefo',
  'contacts.drawer.view': 'Moamohedi',
  'contacts.actions.message': 'Romela molaetsa',
  'contacts.bulk.message': 'Romela molaetsa',
  'contacts.fields.title': 'Dikarolo tsa bokopano',
  'contacts.fields.description': 'Dintlha tse eketsehileng tseo o di bolokang ka motho ka mong ntle le lebitso, nomoro le imeile. Karolo e hlaha foromong ya bokopano, sefeng sa lenane le dikarolong tsa batho, mme e tsamaya le ho kenya le ho ntsha.',
  'contacts.fields.new': 'Karolo e ntjha',
  'contacts.fields.edit': 'Fetola karolo',
  'contacts.fields.empty': 'Ha ho dikarolo tse ikgethileng hajwale',
  'contacts.fields.emptyHint': 'Eketsa karolo bakeng sa eng kapa eng e lokelang ho bolokwa ka motho: nomoro ya odara, leano, letsatsi la ntjhafatso.',
  'contacts.fields.loadError': 'Ha e a kgona ho jarisa dikarolo tsa bokopano.',
  'contacts.fields.columns.label': 'Lebitso',
  'contacts.fields.columns.key': 'Senotlolo',
  'contacts.fields.columns.type': 'Mofuta',
  'contacts.fields.columns.usage': 'Tshebediso',
  'contacts.fields.columns.active': 'Boemo',
  'contacts.fields.usage': 'batho ba {{count}}',
  'contacts.fields.usage.one': 'motho a le 1',
  'contacts.fields.active': 'E a sebetsa',
  'contacts.fields.inactive': 'E patilwe',
  'contacts.fields.type.text': 'Mongolo',
  'contacts.fields.type.number': 'Nomoro',
  'contacts.fields.type.date': 'Letsatsi',
  'contacts.fields.type.boolean': 'E / tjhe',
  'contacts.fields.type.select': 'Kgetho',
  'contacts.fields.type.url': 'Aterese ya webo',
  'contacts.fields.form.label': 'Lebitso',
  'contacts.fields.form.labelHint': 'Seo karolo e bitswang ka sona foromong ya bokopano.',
  'contacts.fields.form.key': 'Senotlolo',
  'contacts.fields.form.keyHint': 'Se latela lebitso ho fihlela o se fetola: ditlhaku tse nyenyane, dinomoro le mela e ka tlase. Se reha kholomo ho kenyeng le ho ntsheng le sebaka sa ho tshwara melaetseng.',
  'contacts.fields.form.keyLocked': 'Senotlolo se ke ke sa fetoha hang ha karolo e le teng: boleng bohle bo bolokilweng ho motho bo kentswe tlasa sona.',
  'contacts.fields.form.type': 'Mofuta',
  'contacts.fields.form.typeHint': 'Kamoo boleng bo bolokwang kateng, le kamoo difene le dikarolo tsa batho di bo bapisang kateng.',
  'contacts.fields.form.typeLocked': 'Mofuta o ke ke wa fetoha hang ha karolo e le teng: boleng bo seng bo bolokilwe bo ne bo tla kgaotsa ho bolela seo bo neng bo se bolela.',
  'contacts.fields.form.options': 'Dikgetho',
  'contacts.fields.form.optionsHint': 'E le nngwe moleng o mong le o mong. Motho o tshwara e le nngwe feela ya tsena.',
  'contacts.fields.form.optionsRequired': 'Karolo ya kgetho e hloka bonyane kgetho e le nngwe.',
  'contacts.fields.form.description': 'Tlhaloso',
  'contacts.fields.form.descriptionHint': 'E bontshwa tlasa karolo foromong ya bokopano.',
  'contacts.fields.form.active': 'E a sebetsa',
  'contacts.fields.form.activeHint': 'Ho tima karolo ho e pata foromong ya bokopano le ho ntsheng. Ha ho letho le bolokilweng ho motho ofe kapa ofe le amehang.',
  'contacts.fields.form.keyInvalid': 'Sebedisa ditlhaku tse nyenyane, dinomoro le mela e ka tlase, o qale ka tlhaku.',
  'contacts.fields.form.keyReserved': 'Motho e mong le e mong o se a ntse a e-na le karolo e nang le lebitso lena.',
  'contacts.fields.form.keyTaken': 'Karolo e nang le senotlolo sena e se e ntse e le teng.',
  'contacts.fields.toast.created': 'Karolo “{{label}}” e eketsitswe',
  'contacts.fields.toast.updated': 'Karolo “{{label}}” e bolokilwe',
  'contacts.fields.toast.deleted': 'Karolo “{{label}}” e tlositswe',
  'contacts.fields.toast.saveFailed': 'Ha e a kgona ho boloka karolo',
  'contacts.fields.toast.deleteFailed': 'Ha e a kgona ho tlosa karolo',
  'contacts.fields.delete.title': 'Tlosa “{{label}}”?',
  'contacts.fields.delete.body': 'Batho ba {{count}} ba na le boleng ba karolo ena. Ho e tlosa ho hlakola boleng boo ho bohle ba bona. Sena se ke ke sa kgutliswa.',
  'contacts.fields.delete.bodyNone': 'Ha ho motho ya nang le boleng ba karolo ena hajwale.',
  'contacts.fields.delete.hint': 'Ho pata karolo o boloke boleng, e time ho e-na le hoo.',
  'contacts.fields.delete.confirm': 'Tlosa karolo',
  'contacts.form.tab.fields': 'Dikarolo',
  'contacts.form.fields.empty': 'Ha ho dikarolo tse ikgethileng hajwale',
  'contacts.form.fields.emptyHint': 'Hlalosa dintlha tse eketsehileng tseo o di bolokang ka motho ka mong mme di hlaha mona.',
  'contacts.form.fields.manage': 'Seta dikarolo tsa bokopano',
  'contacts.form.fields.manageHint': 'Ho haella karolo?',
  'contacts.form.fields.unset': 'Ha e a behwa',
  'contacts.form.fields.loadError': 'Ha e a kgona ho jarisa dikarolo tsa bokopano.',
  'contacts.form.fields.datePlaceholder': 'YYYY-MM-DD',
  'contacts.form.fields.urlPlaceholder': 'https://',
  'contacts.form.fields.invalid.number': 'Kenya nomoro.',
  'contacts.form.fields.invalid.date': 'Kenya letsatsi jwalo ka YYYY-MM-DD.',
  'contacts.form.fields.invalid.url': 'Kenya aterese ya webo e feletseng, e qalang ka http:// kapa https://.',
  'contacts.form.fields.invalid.select': 'Kgetha e nngwe ya dikgetho.',
  'contacts.form.fields.invalid.boolean': 'Kgetha e kapa tjhe.',
  'contacts.form.fields.invalid.unknown': 'Karolo ena ha e sa le teng. Jarisa hape o leke hape.',
  'contacts.filters.field': 'Karolo',
  'contacts.filters.field.any': 'Karolo efe kapa efe',
  'contacts.filters.field.value': 'Boleng',
  'contacts.filters.field.valueAny': 'Boleng bofe kapa bofe',
  'contacts.filters.field.yes': 'E',
  'contacts.filters.field.no': 'Tjhe',
  'contacts.filters.field.placeholder': 'Ngola boleng',
  'contacts.import.preview.mapping': 'Dikholomo',
  'contacts.import.preview.column': 'Faeleng',
  'contacts.import.preview.target': 'E ya ho',
  'contacts.import.preview.target.ignored': 'E hlokomolohilwe',
  'contacts.import.preview.target.unknown': 'Ha e tshwane le letho',
  'contacts.import.preview.target.field': 'Karolo: {{label}}',
  'contacts.import.preview.createFields': 'Theha dikholomo tse {{count}} tse sa tshwaneng le letho e le dikarolo tsa mongolo',
  'contacts.import.preview.createFieldsHint': 'E nngwe le e nngwe e ba karolo e ikgethileng e rehilweng ka hlooho ya yona, mme boleng ba yona bo kenngwa le tse ding. E tlohele e timme ho tlola dikholomo tseo.',
  'contacts.import.preview.fieldProblems': 'Boleng ba {{count}} ha bo tshwanele mofuta wa karolo ya bona. Bo tla bolokwa e le mongolo.',
  'contacts.import.preview.fieldProblem': 'Mola {{row}}, {{column}}: “{{value}}”',
  'contacts.import.doneFieldProblems': 'Boleng ba {{count}} ha bo a tshwanela mofuta wa karolo ya bona mme bo bolokilwe e le mongolo.',
  'contacts.import.format.customFields': 'Kholomo efe kapa efe e nngwe e kopanngwa le karolo e ikgethileng ka senotlolo sa yona kapa lebitso la yona. Dikholomo tse sa tshwaneng le letho di ka thehwa e le dikarolo tsa mongolo ho tswa ponong ya pele.',
  'contacts.import.csv.col.notes': 'Dintlha tsa mongolo o lokolohileng tse bolokilweng ka motho.',
  'contacts.import.format.intro': 'Mola wa pele o tlameha ho ba hlooho e rehang kholomo ka nngwe. Tsena ke dikholomo tseo mokenyi a di tsebang; hlooho efe kapa efe e nngwe e kopanngwa le karolo e ikgethileng.',
  'contacts.importCard.columns': 'Dikholomo: msisdn, name, email, locale, tags, notes, le e le nngwe bakeng sa karolo ka nngwe e ikgethileng. Arola dithekhe ka semicolon.'
};

export default contacts;
